import React from "react";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useTranslation } from "react-i18next";

interface CalendarViewProps {
    currentMonth: CalendarDate;
    selectedDates: Set<string>;
    setCurrentMonth: (date: CalendarDate) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({
    currentMonth,
    selectedDates,
    setCurrentMonth,
}) => {
    const { t } = useTranslation('readyRoster');

    const calendarDateToLocalDate = (date: CalendarDate): string => {
        return `${date.year}-${String(date.month).padStart(2, "0")}-${String(
            date.day
        ).padStart(2, "0")}`;
    };

    const isDateSelected = (date: CalendarDate): boolean => {
        const dateStr = calendarDateToLocalDate(date);
        return selectedDates.has(dateStr);
    };

    const isPastDate = (date: CalendarDate): boolean => {
        return date.compare(today(getLocalTimeZone())) < 0;
    };

    const generateCalendarDays = () => {
        const year = currentMonth.year;
        const month = currentMonth.month;
        const lastDay = new CalendarDate(year, month, 1)
            .add({ months: 1 })
            .subtract({ days: 1 });
        const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
        const days = [];
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(null);
        }
        for (let day = 1; day <= lastDay.day; day++) {
            days.push(new CalendarDate(year, month, day));
        }
        return days;
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(currentMonth.subtract({ months: 1 }));
    };

    const goToNextMonth = () => {
        setCurrentMonth(currentMonth.add({ months: 1 }));
    };

    const formatMonthYear = (date: CalendarDate): string => {
        const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        return `${monthNames[date.month - 1]} ${date.year}`;
    };

    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const calendarDays = generateCalendarDays();

    return (
        <div className="space-y-1 col-span-4">
            <div className="flex items-center justify-between">
                <h4 className="text-xs text-gray-900 ml-4">
                    {t("available_dates")}
                </h4>
                {selectedDates.size > 0 && (
                    <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0 rounded">
                        {t(
                            selectedDates.size === 1
                                ? "date_selected"
                                : "dates_selected",
                            { count: selectedDates.size }
                        )}
                    </div>
                )}
            </div>
            <div className="w-48 h-56 border rounded-md p-1 bg-white ml-5">
                <div className="flex items-center justify-between mb-1">
                    <button
                        onClick={goToPreviousMonth}
                        className="p-2 hover:bg-gray-100 rounded-full flex items-center justify-center"
                        type="button"
                     >
                        <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <h4 className="text-sm font-semibold text-gray-700">
                        {formatMonthYear(currentMonth)}
                    </h4>
                    <button
                        onClick={goToNextMonth}
                        className="p-2 hover:bg-gray-100 rounded-full flex items-center justify-center"
                        type="button"
                    >
                        <svg
                            className="w-3 h-3 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>

                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map((day) => (
                        <div
                            key={day}
                            className="text-xs font-medium text-gray-500 text-center p-1"
                        >
                            {day}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((date, index) => (
                        <div key={index} className="aspect-square">
                            {date ? (
                                <div
                                    className={`
                    w-full h-full text-xs rounded-full flex items-center justify-center font-medium
                    transition-all duration-200
                    ${isPastDate(date)
                                            ? "text-gray-300 cursor-not-allowed"
                                            : "text-gray-700"
                                        }
                    ${isDateSelected(date)
                                            ? "bg-blue-500 text-white"
                                            : ""
                                        }
                    ${date.compare(
                                            today(getLocalTimeZone())
                                        ) === 0 && !isDateSelected(date)
                                            ? "bg-gray-100 font-bold"
                                            : ""
                                        }
                  `}
                                >
                                    {date.day}
                                </div>
                            ) : (
                                <div className="w-full h-full"></div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CalendarView;